/**
 * pageTemplates.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

// Page Template Library
// Add new templates here to make them available in the editor

export const PAGE_TEMPLATES = {
    'tune-page': {
        name: 'Tune Page Template',
        description:
            'Tune page: nav, title-only header, notation, audio player, About + disclaimer, then Featured In / Related Articles / Related Tunes / Also See. On the live site, related blocks are filled in the HTML returned by the API; the script below is fallback (e.g. editor) when data-related-injected is not set.',
        placeholders: {
            TUNE_TITLE: '',
            TUNE_DESCRIPTION: '',
            NOTATION_ASSET_ID: '',
            AUDIO_ASSET_ID: '',
            ORIGINAL_URL: '',
            PAGE_ID: ''
        },
        hints: {
            TUNE_TITLE: 'e.g., Fenella',
            TUNE_DESCRIPTION: 'History, sources, playing notes…',
            NOTATION_ASSET_ID: 'e.g., 903',
            AUDIO_ASSET_ID: 'Optional MP3 asset ID; remove audio block in editor if unused',
            ORIGINAL_URL: '“View original” link (metadata source_url also used)',
            PAGE_ID: 'Filled from page slug when you save'
        },
        template: `<!-- Start of Tune Page Content -->

<style>
    /* ── Base ─────────────────────────────────────────────────────────── */
    .tune-page {
        max-width: 900px;
        margin: 0 auto;
        padding: 6px 12px;
        background-color: var(--color-background);
        color: var(--color-text-primary);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }

    /* ── Navigation Bar ───────────────────────────────────────────────── */
    .tune-page .tune-nav-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 8px;
        padding-bottom: 6px;
        border-bottom: 1px solid var(--color-border);
    }
    .tune-page .tune-nav-bar a {
        text-decoration: none;
        font-size: 0.85em;
        transition: opacity 0.2s;
    }
    .tune-page .tune-nav-bar a:hover { opacity: 0.7; }
    .tune-page .nav-link-original { color: var(--color-secondary); }
    .tune-page .nav-link-back      { color: var(--color-accent); font-weight: 500; }

    /* ── Tune Header ──────────────────────────────────────────────────── */
    .tune-page .tune-header {
        text-align: center;
        margin-bottom: 8px;
    }
    .tune-page .tune-title {
        color: var(--color-primary);
        font-size: 2em;
        margin: 0;
        line-height: 1.2;
        font-weight: 700;
    }

    /* ── Notation ─────────────────────────────────────────────────────── */
    .tune-page .tune-notation {
        text-align: center;
        margin: 8px 0;
    }
    .tune-page .tune-notation img {
        max-width: 100%;
        height: auto;
        border: 1px solid var(--color-border);
        padding: 4px;
        background: var(--color-surface);
        border-radius: 6px;
        box-shadow: 0 2px 6px var(--color-shadow);
    }

    /* ── Audio ────────────────────────────────────────────────────────── */
    .tune-page .tune-audio {
        text-align: center;
        margin: 6px 0 10px 0;
        padding: 8px 12px;
        background: var(--color-surface);
        border-radius: 8px;
        border: 1px solid var(--color-border);
    }
    /* Hide audio section only when data-audio-asset is explicitly empty (apply-template removed it) */
    .tune-page .tune-audio[data-audio-asset=""] { display: none; }
    .tune-page .tune-audio audio {
        width: 100%;
        max-width: 480px;
        border-radius: 20px;
        display: block;
        margin: 0 auto;
    }

    /* ── Shared section card ──────────────────────────────────────────── */
    .tune-page .tune-description,
    .tune-page .related-products-section,
    .tune-page .related-articles-section,
    .tune-page .related-tunes,
    .tune-page .curated-section {
        margin: 10px 0;
        padding: 12px 14px;
        background: var(--color-surface);
        border-radius: 8px;
        border: 1px solid var(--color-border);
        box-shadow: 0 2px 6px var(--color-shadow);
    }

    /* ── Section headers (shared style) ──────────────────────────────── */
    .tune-page .tune-section-header {
        color: var(--color-primary);
        border-bottom: 2px solid var(--color-primary);
        padding-bottom: 5px;
        margin: 0 0 8px 0;
        font-size: 1.12em;
        font-weight: 600;
    }
    /* About: keep heading close to body copy (avoid double gap with pre-line) */
    .tune-page .tune-description > .tune-section-header {
        margin-bottom: 0.4rem;
    }

    /* ── Paul Cranford disclaimer (bottom of About This Tune) ─────────── */
    .tune-page .tune-description .tune-fine-print {
        margin: 1rem auto 0;
        max-width: 42rem;
        font-size: 0.78rem;
        color: var(--color-text-secondary, #888);
        font-style: italic;
        line-height: 1.6;
        text-align: center;
        padding: 0;
        border-top: none;
    }
    /* Lightweight full-width rule — sits at section edge, not paragraph width */
    .tune-page .tune-description .tune-fine-print::before {
        content: '';
        display: block;
        border-top: 1px solid var(--color-border, #e5e7eb);
        margin: 0 -14px 0.75rem -14px;
    }

    /* ── About This Tune ──────────────────────────────────────────────── */
    .tune-page .tune-description { line-height: 1.65; }
    .tune-page .tune-description-text {
        color: var(--color-text-primary);
        font-size: 0.95em;
        white-space: normal;
        margin-top: 0;
    }

    /* ── Featured In (image-only covers) ─────────────────────────────── */
    .tune-page .related-products-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 112px), 1fr));
        gap: 0.35rem;
        margin-top: 0.35rem;
    }

    /* ── Related Articles ─────────────────────────────────────────────── */
    .tune-page .related-articles-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 6px;
    }
    .tune-page .related-article-card {
        border: 1px solid var(--color-border);
        border-radius: 8px;
        background: var(--color-background);
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
        overflow: hidden;
    }
    .tune-page .related-article-card:hover {
        border-color: var(--color-primary);
        box-shadow: 0 2px 10px var(--color-shadow);
    }
    .tune-page .related-article-link {
        display: block;
        padding: 8px 12px;
        text-decoration: none;
        color: inherit;
    }
    .tune-page .related-article-title {
        font-weight: 600;
        font-size: 0.95rem;
        color: var(--color-primary);
        margin-bottom: 4px;
    }
    .tune-page .related-article-desc {
        font-size: 0.82rem;
        color: var(--color-text-secondary);
        line-height: 1.45;
    }
    .tune-page .related-article-type {
        display: inline-block;
        font-size: 0.72rem;
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: 4px;
        padding: 1px 6px;
        margin-top: 5px;
        color: var(--color-text-secondary);
    }

    /* ── Curated Picks ────────────────────────────────────────────────── */
    .tune-page .curated-section .curated-grid {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 6px;
    }
    .tune-page .curated-card {
        border: 1px solid var(--color-border);
        border-radius: 8px;
        background: var(--color-background);
        transition: border-color 0.2s, box-shadow 0.2s;
    }
    .tune-page .curated-card:hover { border-color: var(--color-primary); box-shadow: 0 2px 10px var(--color-shadow); }
    .tune-page .curated-card a {
        display: flex; align-items: center; gap: 8px;
        padding: 8px 10px; text-decoration: none; color: inherit;
    }
    .tune-page .curated-card-icon { font-size: 1.1rem; flex-shrink: 0; }
    .tune-page .curated-card-body { flex: 1; min-width: 0; }
    .tune-page .curated-card-title { font-weight: 600; font-size: 0.9rem; color: var(--color-primary); }
    .tune-page .curated-card-desc  { font-size: 0.78rem; color: var(--color-text-secondary); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .tune-page .curated-card-type  { font-size: 0.7rem; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 4px; padding: 1px 6px; white-space: nowrap; flex-shrink: 0; color: var(--color-text-secondary); }

    /* ── Related Tunes ────────────────────────────────────────────────── */
    .tune-page .related-content-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 8px;
        margin-top: 6px;
    }

    /* ── Placeholder ──────────────────────────────────────────────────── */
    .tune-page .related-placeholder {
        text-align: center;
        padding: 14px;
        color: var(--color-text-secondary);
        grid-column: 1 / -1;
    }
    .tune-page .related-placeholder-icon {
        font-size: 1.5em;
        margin-bottom: 8px;
        opacity: 0.6;
    }

    /* ── Product Cards ────────────────────────────────────────────────── */
    .tune-page .related-product-card {
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: 8px;
        overflow: hidden;
        transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    .tune-page .related-product-card:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 20px var(--color-shadow);
        border-color: var(--color-primary);
    }
    .tune-page .related-product-link {
        text-decoration: none;
        color: inherit;
        display: flex;
        flex-direction: column;
        flex: 1;
    }
    .tune-page .related-product-image {
        width: 100%;
        height: 180px;
        overflow: hidden;
        background: var(--color-background);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .tune-page .related-product-image img {
        width: 100%; height: 100%; object-fit: cover;
    }
    .tune-page .related-product-info {
        padding: 0.875rem;
        flex: 1;
        display: flex;
        flex-direction: column;
    }
    .tune-page .related-product-name {
        color: var(--color-text-primary);
        font-size: 0.95rem;
        font-weight: 600;
        margin: 0 0 0.4rem 0;
        line-height: 1.3;
        flex: 1;
    }
    .tune-page .related-product-author {
        color: var(--color-text-secondary);
        font-size: 0.8rem;
        font-style: italic;
        margin: 0 0 0.5rem 0;
    }
    .tune-page .related-product-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: auto;
        gap: 0.5rem;
        flex-wrap: wrap;
    }
    .tune-page .related-product-price {
        color: var(--color-primary);
        font-size: 1rem;
        font-weight: 700;
    }
    .tune-page .related-product-tags { display: flex; gap: 0.25rem; flex-wrap: wrap; }
    .tune-page .product-tag {
        background: var(--color-secondary);
        color: var(--color-background);
        padding: 0.15rem 0.4rem;
        border-radius: 4px;
        font-size: 0.68rem;
        font-weight: 500;
        text-transform: lowercase;
    }

    /* Featured In: book-style covers — image only (name on link title) */
    .tune-page .related-product-card--cover-only {
        padding: 0;
        background: transparent;
        box-shadow: none;
        border: none;
    }
    .tune-page .related-product-card--cover-only:hover { transform: translateY(-1px); }
    .tune-page .related-product-link--cover-only {
        display: block;
        text-decoration: none;
        color: inherit;
        line-height: 0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 3px var(--color-shadow, rgba(0,0,0,0.08));
        transition: box-shadow 0.2s ease, transform 0.2s ease;
    }
    .tune-page .related-product-card--cover-only:hover .related-product-link--cover-only {
        box-shadow: 0 4px 14px var(--color-shadow, rgba(0,0,0,0.12));
    }
    .tune-page .related-product-card--cover-only .related-product-image {
        border-radius: 8px;
        aspect-ratio: 2 / 3;
        max-height: 200px;
        width: 100%;
        height: auto;
        min-height: 140px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        box-sizing: border-box;
        overflow: hidden;
    }
    .tune-page .related-product-card--cover-only .related-product-image img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
    .tune-page .related-product-cover-title { display: none !important; }
    .tune-page .related-product-image--empty {
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-surface);
        min-height: 160px;
    }
    .tune-page .related-product-fallback {
        font-size: 2rem;
        opacity: 0.45;
        line-height: 1;
    }

    /* Featured In: older pages with full product cards → show cover only */
    .tune-page .related-products-grid .related-product-info {
        display: none !important;
    }
    .tune-page .related-products-grid .related-product-card:not(.related-product-card--cover-only) {
        padding: 0;
        background: transparent;
        box-shadow: none;
        border: none;
    }
    .tune-page .related-products-grid .related-product-card:not(.related-product-card--cover-only) .related-product-link {
        display: block;
        line-height: 0;
    }
    .tune-page .related-products-grid .related-product-card:not(.related-product-card--cover-only) .related-product-image {
        border-radius: 6px;
        height: 268px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        box-sizing: border-box;
        overflow: hidden;
    }
    .tune-page .related-products-grid .related-product-card:not(.related-product-card--cover-only) .related-product-image img {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }

    /* ── Tune Cards ───────────────────────────────────────────────────── */
    .tune-page .related-tune-card {
        display: block;
        background: var(--color-card-bg, var(--color-surface));
        border: 1px solid var(--color-border);
        border-radius: 8px;
        padding: 10px;
        text-decoration: none;
        color: var(--color-text-primary);
        transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        box-shadow: 0 1px 3px var(--color-shadow);
    }
    .tune-page .related-tune-card:hover {
        border-color: var(--color-accent);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px var(--color-shadow);
    }
    .tune-page .tune-card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 6px;
    }
    .tune-page .tune-card-title  { color: var(--color-text-primary); font-size: 1em; margin: 0; font-weight: 600; line-height: 1.3; flex: 1; }
    .tune-page .tune-card-score  { background: var(--color-secondary); color: var(--color-background); padding: 2px 5px; border-radius: 3px; font-size: 0.68em; font-weight: 600; margin-left: 6px; }
    .tune-page .tune-card-composer { color: var(--color-text-secondary); font-size: 0.78em; margin: 0 0 4px 0; font-style: italic; line-height: 1.25; }
    .tune-page .tune-card-meta   { color: var(--color-text-secondary); font-size: 0.8em; margin: 0 0 5px 0; }
    .tune-page .tune-card-album  { color: var(--color-success); font-size: 0.72em; margin: 0 0 5px 0; }
    .tune-page .tune-card-tags   { display: flex; flex-wrap: wrap; gap: 4px; }
    .tune-page .tune-card-tag    { background: var(--color-surface-hover); color: var(--color-text-primary); padding: 2px 5px; border-radius: 3px; font-size: 0.68em; }

    /* ── Responsive ───────────────────────────────────────────────────── */
    @media (max-width: 600px) {
        .tune-page {
            padding: 5px 10px;
        }
        .tune-page .tune-title {
            font-size: 1.5em;
        }
        .tune-page .tune-section-header {
            font-size: 1.05em;
        }
        .tune-page .tune-description,
        .tune-page .related-products-section,
        .tune-page .related-articles-section,
        .tune-page .related-tunes,
        .tune-page .curated-section {
            margin: 8px 0;
            padding: 10px 12px;
            border-radius: 8px;
        }
        .tune-page .tune-audio {
            margin: 6px 0 10px 0;
            padding: 8px 10px;
        }
        .tune-page .related-content-grid {
            grid-template-columns: 1fr;
            gap: 7px;
            margin-top: 6px;
        }
        .tune-page .related-products-grid {
            grid-template-columns: repeat(auto-fill, minmax(min(100%, 96px), 1fr));
            gap: 0.3rem;
        }
        .tune-page .related-product-image { height: 150px; }
        .tune-page .related-product-card--cover-only .related-product-image,
        .tune-page .related-products-grid .related-product-card:not(.related-product-card--cover-only) .related-product-image {
            max-height: 168px;
            min-height: 120px;
        }
        .tune-page .related-placeholder   { padding: 12px; }
        .tune-page .related-product-card  { touch-action: manipulation; }
        .tune-page .tune-description .tune-fine-print::before {
            margin-left: -12px;
            margin-right: -12px;
            margin-bottom: 0.65rem;
        }
    }
</style>

<div class="tune-page" data-page-id="{{PAGE_ID}}">

    <!-- Top Navigation Bar -->
    <div class="tune-nav-bar">
        <!-- Original URL --><a href="{{ORIGINAL_URL}}" target="_blank" rel="noopener noreferrer" class="nav-link-original">📎 View Original Page</a><!-- /Original URL -->
        <a href="/tunes" class="nav-link-back">
            ← Back to Tunes
        </a>
    </div>
    
    <!-- Tune Header -->
    <div class="tune-header">
        <h1 class="tune-title">
            {{TUNE_TITLE}}
        </h1>
    </div>
  
    <!-- Musical Notation -->
    <div class="tune-notation">
        <img src="/api/assets/{{NOTATION_ASSET_ID}}/thumbnail" 
             alt="{{TUNE_TITLE}} musical notation"
             onerror="this.closest('.tune-notation').style.display='none';"
        >
    </div>
    
    <!-- Audio Section Start -->
    <div class="tune-audio" data-audio-asset="{{AUDIO_ASSET_ID}}">
        <audio controls preload="metadata">
            <source src="/api/assets/{{AUDIO_ASSET_ID}}/download" type="audio/mpeg">
            Your browser does not support the audio element.
        </audio>
    </div>
    <!-- Audio Section End -->
    
    <!-- About This Tune -->
    <div class="tune-description">
        <h2 class="tune-section-header">📝 About This Tune</h2>
        <div class="tune-description-text">{{TUNE_DESCRIPTION}}</div>
        <p class="tune-fine-print">Many of the tunes posted on this site are Paul Cranford's initial drafts. While many have already been featured in his published collections, others are currently being refined for future release through Cranford Publications.</p>
    </div>

    <!-- Related sections: filled server-side on GET for public pages, or by the script below -->
    <div class="related-products-section">
        <h2 class="tune-section-header" data-related-section="products">📚 Featured In</h2>
        <div id="related-products-content" class="related-products-grid"></div>
    </div>

    <div class="related-articles-section">
        <h2 class="tune-section-header" data-related-section="articles">📰 Related Articles</h2>
        <div id="related-articles-content" class="related-articles-list"></div>
    </div>

    <div class="related-tunes">
        <h2 class="tune-section-header" data-related-section="tunes">🎵 Related Tunes</h2>
        <div id="related-tunes-content" class="related-content-grid"></div>
    </div>

    <div class="curated-section">
        <h2 class="tune-section-header" id="curated-section-title" data-related-section="curated">🔗 Also See</h2>
        <div id="curated-content" class="curated-grid"></div>
    </div>
    
</div> <!-- .tune-page -->

<script>
(function() {
    /* Related blocks already in HTML from API when data-related-injected="1" (public tune GET). */
    var _tp     = document.querySelector('.tune-page');
    if (_tp && _tp.getAttribute('data-related-injected') === '1') {
        return;
    }
    var _pageId = (_tp && _tp.dataset.pageId) || '';

    if (!_pageId) {
        var parts = window.location.pathname.split('/');
        _pageId = parts[parts.length - 1] || parts[parts.length - 2] || '';
    }

    var API = '/api/content';
    /* Max items per related section (overridden from page metadata related_content.section_limits) */
    var SECTION_MAX = { products: 24, articles: 8, tunes: 12, curated: 24 };

    function hideSect(sel) { var el = document.querySelector(sel); if (el) el.style.display = 'none'; }

    function mergeSectionLimits(cfg) {
        if (!cfg || !cfg.section_limits) return;
        ['products', 'articles', 'tunes', 'curated'].forEach(function(k) {
            var v = parseInt(cfg.section_limits[k], 10);
            if (v >= 1 && v <= 100) SECTION_MAX[k] = v;
        });
    }

    function applySectionTitles(cfg) {
        if (!cfg || !cfg.section_titles) return;
        var fb = {
            products: '.related-products-section h2.tune-section-header',
            articles: '.related-articles-section h2.tune-section-header',
            tunes: '.related-tunes h2.tune-section-header',
            curated: '#curated-section-title, .curated-section h2.tune-section-header'
        };
        ['products', 'articles', 'tunes', 'curated'].forEach(function(key) {
            var title = cfg.section_titles[key];
            if (title == null || title === '') return;
            var el = document.querySelector('[data-related-section="' + key + '"]') || (fb[key] ? document.querySelector(fb[key]) : null);
            if (el) el.textContent = String(title);
        });
    }

    function init() {
        fetch(API + '/tune-sections/' + _pageId)
            .then(function(r) { return r.ok ? r.json() : null; })
            .then(function(cfg) {
                if (!cfg) { hideAll(); return; }
                mergeSectionLimits(cfg);
                applySectionTitles(cfg);
                var s = cfg.sections || {};
                runSection('products', s.products || 'explicit',  cfg);
                runSection('articles', s.articles || 'off',       cfg);
                runSection('tunes',    s.tunes    || 'tags',      cfg);
                runSection('curated',  s.curated  || 'off',       cfg);
            })
            .catch(function() { hideAll(); });
    }

    function hideAll() {
        hideSect('.related-products-section');
        hideSect('.related-articles-section');
        hideSect('.related-tunes');
        hideSect('.curated-section');
    }

    function runSection(type, mode, cfg) {
        if (mode === 'off') {
            var sel = { products:'.related-products-section', articles:'.related-articles-section',
                        tunes:'.related-tunes', curated:'.curated-section' };
            hideSect(sel[type]); return;
        }
        if (type === 'products') loadProducts(mode, cfg);
        if (type === 'articles') loadArticles(mode, cfg);
        if (type === 'tunes')    loadTunes(mode, cfg);
        if (type === 'curated')  loadCurated(cfg);
    }

    // ── Products ──────────────────────────────────────────────────────────────
    function loadProducts(mode, cfg) {
        fetch(API + '/tunes/' + _pageId + '/products')
            .then(function(r) { return r.ok ? r.json() : []; })
            .then(displayProducts)
            .catch(function() { hideSect('.related-products-section'); });
    }

    function displayProducts(list) {
        var c = document.getElementById('related-products-content');
        if (!c) return;
        if (!list || !list.length) { hideSect('.related-products-section'); return; }
        list = list.slice(0, SECTION_MAX.products);
        c.innerHTML = list.map(function(p) {
            var img = p.image
                ? '<div class="related-product-image"><img src="' + p.image + '" alt="" loading="lazy" onerror="this.parentNode.classList.add(' + "'related-product-image--empty'" + ')"></div>'
                : '<div class="related-product-image related-product-image--empty" aria-hidden="true"><span class="related-product-fallback">📚</span></div>';
            return '<div class="related-product-card related-product-card--cover-only">' +
                '<a href="/product/' + p.id + '" class="related-product-link related-product-link--cover-only" title="' + esc(p.name) + '" aria-label="View product: ' + esc(p.name) + '">' +
                img +
                '</a></div>';
        }).join('');
    }

    // ── Articles ──────────────────────────────────────────────────────────────
    function loadArticles(mode, cfg) {
        var promises = [];
        if (mode === 'explicit' || mode === 'both') {
            promises.push(
                fetch(API + '/tunes/' + _pageId + '/articles')
                    .then(function(r) { return r.ok ? r.json() : []; }).catch(function() { return []; })
            );
        }
        if (mode === 'tags' || mode === 'both') {
            var tags = cfg.pageTags || '';
            if (tags.trim()) {
                var artLim = Math.min(100, Math.max(SECTION_MAX.articles, 8));
                promises.push(
                    fetch(API + '/related?tags=' + encodeURIComponent(tags) + '&limit=' + artLim + '&exclude=' + encodeURIComponent(_pageId) + '&content_type=article')
                        .then(function(r) { return r.ok ? r.json() : []; }).catch(function() { return []; })
                );
            }
        }
        Promise.all(promises).then(function(results) {
            var seen = new Set();
            var merged = [];
            results.forEach(function(arr) {
                arr.forEach(function(a) {
                    var id = a.pageId || a.page_id;
                    if (!seen.has(id)) { seen.add(id); merged.push(a); }
                });
            });
            displayArticles(merged);
        }).catch(function() { hideSect('.related-articles-section'); });
    }

    function displayArticles(list) {
        var c = document.getElementById('related-articles-content');
        if (!c) return;
        if (!list || !list.length) { hideSect('.related-articles-section'); return; }
        list = list.slice(0, SECTION_MAX.articles);
        c.innerHTML = list.map(function(a) {
            var id  = a.pageId || a.page_id;
            var url = a.url || ('/page/' + id);
            var desc = (a.description || '').slice(0, 140);
            return '<div class="related-article-card">' +
                '<a href="' + url + '" class="related-article-link">' +
                '<div class="related-article-title">' + esc(a.title || id) + '</div>' +
                (desc ? '<div class="related-article-desc">' + esc(desc) + (a.description && a.description.length > 140 ? '\u2026' : '') + '</div>' : '') +
                '</a></div>';
        }).join('');
    }

    // ── Tunes ─────────────────────────────────────────────────────────────────
    function loadTunes(mode, cfg) {
        var promises = [];
        if ((mode === 'explicit' || mode === 'both') && cfg.explicitTunes && cfg.explicitTunes.length) {
            promises.push(
                fetch(API + '/pages-by-ids?ids=' + encodeURIComponent(cfg.explicitTunes.join(',')))
                    .then(function(r) { return r.ok ? r.json() : []; })
                    .then(function(pages) {
                        return pages.map(function(p) {
                            return { page_id: p.page_id, title: p.title,
                                region: (p.metadata && p.metadata.region) || '',
                                tune_type: (p.metadata && p.metadata.tune_type) || '',
                                artist: (p.metadata && p.metadata.artist) || '',
                                album: (p.metadata && p.metadata.album) || '',
                                tags: (p.metadata && p.metadata.tags) || '', score: 100 };
                        });
                    }).catch(function() { return []; })
            );
        }
        if (mode === 'tags' || mode === 'both') {
            promises.push(
                fetch(API + '/tunes/related/' + _pageId)
                    .then(function(r) { return r.ok ? r.json() : []; }).catch(function() { return []; })
            );
        }
        Promise.all(promises).then(function(results) {
            var seen = new Set();
            var merged = [];
            results.forEach(function(arr) {
                arr.forEach(function(t) {
                    if (!seen.has(t.page_id)) { seen.add(t.page_id); merged.push(t); }
                });
            });
            displayTunes(merged);
        }).catch(function() { hideSect('.related-tunes'); });
    }

    function displayTunes(list) {
        var c = document.getElementById('related-tunes-content');
        if (!c) return;
        if (!list || !list.length) { hideSect('.related-tunes'); return; }
        var filtered = list.filter(function(t) { return !t.score || t.score >= 15; });
        if (!filtered.length) { hideSect('.related-tunes'); return; }
        filtered = filtered.slice(0, SECTION_MAX.tunes);
        c.innerHTML = filtered.map(function(t) {
            var tagsArr = typeof t.tags === 'string'
                ? t.tags.split(',').map(function(x){return x.trim();}).filter(Boolean)
                : (Array.isArray(t.tags) ? t.tags.map(String) : []);
            var compStr = '';
            if (t.composer != null && t.composer !== '') {
                compStr = Array.isArray(t.composer)
                    ? t.composer.map(function(x){ return String(x).trim(); }).filter(Boolean).join(', ')
                    : String(t.composer).trim();
            }
            var composerHtml = compStr ? '<p class="tune-card-composer">' + esc(compStr) + '</p>' : '';
            return '<a href="/page/' + t.page_id + '" class="related-tune-card">' +
                '<div class="tune-card-header"><h3 class="tune-card-title">' + esc(t.title) + '</h3>' +
                (t.score && t.score < 100 ? '<span class="tune-card-score">' + t.score.toFixed(1) + '</span>' : '') +
                '</div>' + composerHtml + '<p class="tune-card-meta">' + esc(t.region || 'Traditional') + ' \u2022 ' + esc(t.tune_type || 'Tune') + '</p>' +
                (t.album  ? '<p class="tune-card-album">\uD83D\uDCBF ' + esc(t.album) + '</p>' : '') +
                (tagsArr.length ? '<div class="tune-card-tags">' + tagsArr.slice(0,4).map(function(x){ return '<span class="tune-card-tag">' + esc(x) + '</span>'; }).join('') + '</div>' : '') +
                '</a>';
        }).join('');
    }

    // ── Curated Picks ─────────────────────────────────────────────────────────
    function loadCurated(cfg) {
        var items = cfg.curatedItems || [];
        if (!items.length) { hideSect('.curated-section'); return; }

        var tuneIds    = items.filter(function(i){return i.type==='tune';}).map(function(i){return i.id;});
        var articleIds = items.filter(function(i){return i.type==='article';}).map(function(i){return i.id;});
        /* tune-asset + route need no pages-by-ids fetch */

        var promises = [
            tuneIds.length
                ? fetch(API + '/pages-by-ids?ids=' + encodeURIComponent(tuneIds.join(','))).then(function(r){return r.ok?r.json():[];}).catch(function(){return [];})
                : Promise.resolve([]),
            articleIds.length
                ? fetch(API + '/pages-by-ids?ids=' + encodeURIComponent(articleIds.join(','))).then(function(r){return r.ok?r.json():[];}).catch(function(){return [];})
                : Promise.resolve([]),
        ];

        Promise.all(promises).then(function(results) {
            var pageMap = {};
            results[0].forEach(function(p){ pageMap[p.page_id] = p; });
            results[1].forEach(function(p){ pageMap[p.page_id] = p; });

            var ordered = items.map(function(item) {
                if (item.type === 'product') return { _item: item, type: 'product', id: item.id };
                if (item.type === 'route' && item.href) {
                    return { _item: item, type: 'route', href: item.href, title: item.title || item.href };
                }
                if (item.type === 'tune-asset' && item.id != null) {
                    return { _item: item, type: 'tune-asset', id: item.id, title: item.title || ('ABC #' + item.id) };
                }
                var p = pageMap[item.id];
                if (!p) return null;
                return { _item: item, type: item.type, page_id: p.page_id, title: p.title, description: p.description };
            }).filter(Boolean);

            displayCurated(ordered);
        }).catch(function() { hideSect('.curated-section'); });
    }

    function displayCurated(orderedItems) {
        var c = document.getElementById('curated-content');
        if (!c) return;
        if (!orderedItems.length) { hideSect('.curated-section'); return; }
        orderedItems = orderedItems.slice(0, SECTION_MAX.curated);

        var icons  = { tune: '\uD83C\uDFB5', article: '\uD83D\uDCF0', product: '\uD83D\uDCDA', route: '\uD83D\uDD17', 'tune-asset': '\uD83C\uDFBC' };
        var labels = { tune: 'Tune', article: 'Page', product: 'Book / Recording', route: 'Link', 'tune-asset': 'ABC' };

        c.innerHTML = orderedItems.map(function(item) {
            var icon  = icons[item.type]  || '\uD83D\uDD17';
            var label = labels[item.type] || item.type;
            var title = item.type === 'product' ? 'Product #' + item.id
                : item.type === 'route' ? (item.title || item.href || '')
                : item.type === 'tune-asset' ? (item.title || ('ABC #' + item.id))
                : (item.title || item.page_id || item.id);
            var desc  = (item.type !== 'product' && item.type !== 'route' && item.type !== 'tune-asset') ? (item.description || '').slice(0, 120) : '';
            var href  = item.type === 'product' ? '/product/' + item.id
                : item.type === 'route' ? item.href
                : item.type === 'tune-asset' ? ('/tune-asset/' + item.id)
                : '/page/' + item.page_id;
            return '<div class="curated-card">' +
                '<a href="' + href + '">' +
                '<span class="curated-card-icon">' + icon + '</span>' +
                '<div class="curated-card-body">' +
                '<div class="curated-card-title">' + esc(title) + '</div>' +
                (desc ? '<div class="curated-card-desc">' + esc(desc) + '</div>' : '') +
                '</div>' +
                '<span class="curated-card-type">' + label + '</span>' +
                '</a></div>';
        }).join('');
    }

    function esc(text) {
        if (!text) return '';
        var map = { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#039;' };
        return text.toString().replace(/[&<>"']/g, function(m){ return map[m]; });
    }
    window.escapeHtml = esc;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
    setTimeout(function() {
        var c = document.getElementById('related-products-content');
        if (c && c.querySelector('.related-placeholder')) init();
    }, 500);
})();
</script>

<!-- End of Tune Page Content -->`
    },

    'abc-tune-page': {
        name: 'ABC Tune Page Template',
        description: 'Tune page that links to the interactive ABC player (/tune-asset/{id}). Set page type to ABC Notation.',
        placeholders: {
            TUNE_TITLE: '',
            ABC_INTRO: '',
            ABC_ASSET_ID: '',
            AUDIO_ASSET_ID: '',
            TUNE_DESCRIPTION: '',
            ORIGINAL_URL: '',
            PAGE_ID: ''
        },
        hints: {
            TUNE_TITLE: 'e.g., The High Road to Linton',
            ABC_INTRO: 'e.g., Open the player for sheet music, playback, tempo, and downloads.',
            ABC_ASSET_ID: 'Numeric ID of the ABC file asset (Admin → Assets)',
            AUDIO_ASSET_ID: 'Optional MP3 asset ID; delete the Audio block in the editor if unused',
            TUNE_DESCRIPTION: 'History, sources, playing notes…',
            ORIGINAL_URL: 'Optional “view original” link',
            PAGE_ID: 'Filled from page slug when you save'
        },
        template: `<!-- Start of ABC Tune Page Content -->

<style>
    .abc-tune-page {
        max-width: 900px;
        margin: 0 auto;
        padding: 10px 20px;
        background-color: var(--color-background);
        color: var(--color-text-primary);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }
    .abc-tune-page .abc-nav-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 12px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--color-border);
    }
    .abc-tune-page .abc-nav-bar a {
        text-decoration: none;
        font-size: 0.85em;
        transition: opacity 0.2s;
    }
    .abc-tune-page .abc-nav-bar a:hover { opacity: 0.7; }
    .abc-tune-page .nav-link-original { color: var(--color-secondary); }
    .abc-tune-page .nav-link-back { color: var(--color-accent); font-weight: 500; }
    .abc-tune-page .abc-header { text-align: center; margin-bottom: 12px; }
    .abc-tune-page .abc-title {
        color: var(--color-primary);
        font-size: 2em;
        margin: 0;
        line-height: 1.2;
        font-weight: 700;
    }
    .abc-tune-page .abc-player-panel {
        text-align: center;
        margin: 16px 0 20px;
        padding: 22px 20px;
        background: var(--color-surface);
        border-radius: 10px;
        border: 1px solid var(--color-border);
        box-shadow: 0 2px 6px var(--color-shadow);
    }
    .abc-tune-page .abc-player-intro {
        margin: 0 0 14px 0;
        color: var(--color-text-secondary);
        font-size: 0.95em;
        line-height: 1.5;
    }
    .abc-tune-page .abc-open-player {
        display: inline-block;
        padding: 12px 22px;
        background: var(--color-primary);
        color: var(--color-background);
        font-weight: 600;
        font-size: 0.95em;
        text-decoration: none;
        border-radius: 8px;
        transition: opacity 0.2s, transform 0.15s;
    }
    .abc-tune-page .abc-open-player:hover { opacity: 0.92; transform: translateY(-1px); }
    .abc-tune-page .abc-audio {
        text-align: center;
        margin: 12px 0 20px 0;
        padding: 12px 16px;
        background: var(--color-surface);
        border-radius: 10px;
        border: 1px solid var(--color-border);
    }
    /* Hide audio section only when data-audio-asset is explicitly empty */
    .abc-tune-page .abc-audio[data-audio-asset=""] { display: none; }
    .abc-tune-page .abc-audio audio {
        width: 100%;
        max-width: 480px;
        border-radius: 20px;
        display: block;
        margin: 0 auto;
    }
    .abc-tune-page .abc-description {
        margin: 18px 0;
        padding: 20px;
        background: var(--color-surface);
        border-radius: 10px;
        border: 1px solid var(--color-border);
        line-height: 1.65;
    }
    .abc-tune-page .abc-section-header {
        color: var(--color-primary);
        border-bottom: 2px solid var(--color-primary);
        padding-bottom: 7px;
        margin: 0 0 14px 0;
        font-size: 1.25em;
        font-weight: 600;
    }
    .abc-tune-page .abc-description-text {
        color: var(--color-text-primary);
        font-size: 0.95em;
        white-space: pre-line;
    }
    .abc-tune-page .abc-description .abc-fine-print {
        margin: 1rem auto 0;
        max-width: 42rem;
        font-size: 0.75rem;
        color: var(--color-text-secondary, #888);
        font-style: italic;
        line-height: 1.5;
        text-align: center;
        padding: 0.75rem 0.75rem 0;
        border-top: none;
    }
    .abc-tune-page .abc-description .abc-fine-print::before {
        content: '';
        display: block;
        border-top: 2px solid var(--color-primary);
        margin: 0 -20px 0.65rem -20px;
    }
    @media (max-width: 600px) {
        .abc-tune-page { padding: 8px 12px; }
        .abc-tune-page .abc-title { font-size: 1.5em; }
        .abc-tune-page .abc-player-panel { padding: 16px 14px; }
    }
</style>

<div class="abc-tune-page" data-page-id="{{PAGE_ID}}">

    <div class="abc-nav-bar">
        <a href="{{ORIGINAL_URL}}" target="_blank" rel="noopener noreferrer" class="nav-link-original">📎 View Original Page</a>
        <a href="/tunes" class="nav-link-back">← Back to Tunes</a>
    </div>

    <div class="abc-header">
        <h1 class="abc-title">{{TUNE_TITLE}}</h1>
    </div>

    <div class="abc-player-panel">
        <p class="abc-player-intro">{{ABC_INTRO}}</p>
        <a class="abc-open-player" href="/tune-asset/{{ABC_ASSET_ID}}">🎼 Open ABC notation &amp; player</a>
    </div>

    <!-- Audio Section Start -->
    <div class="abc-audio" data-audio-asset="{{AUDIO_ASSET_ID}}">
        <audio controls preload="metadata">
            <source src="/api/assets/{{AUDIO_ASSET_ID}}/download" type="audio/mpeg">
            Your browser does not support the audio element.
        </audio>
    </div>
    <!-- Audio Section End -->

    <div class="abc-description">
        <h2 class="abc-section-header">📝 About This Tune</h2>
        <div class="abc-description-text">
{{TUNE_DESCRIPTION}}
        </div>
        <p class="abc-fine-print">Many of the tunes posted on this site are Paul Cranford's initial drafts. While many have already been featured in his published collections, others are currently being refined for future release through Cranford Publications.</p>
    </div>

</div>

<!-- End of ABC Tune Page Content -->`
    },

    'article-page': {
        name: 'Article Page Template',
        description: 'General article with featured image, author, and date',
        placeholders: {
            ARTICLE_TITLE: '',
            ARTICLE_SUBTITLE: '',
            ARTICLE_AUTHOR: '',
            ARTICLE_DATE: '',
            FEATURED_IMAGE_ASSET_ID: '',
            FEATURED_IMAGE_CAPTION: '',
            ARTICLE_CONTENT: '',
            ARTICLE_TAGS: ''
        },
        hints: {
            ARTICLE_TITLE: 'e.g., The History of Cape Breton Fiddle Music',
            ARTICLE_SUBTITLE: 'e.g., A journey through centuries of tradition',
            ARTICLE_AUTHOR: 'e.g., Paul Cranford',
            ARTICLE_DATE: 'e.g., January 15, 2026',
            FEATURED_IMAGE_ASSET_ID: 'e.g., 123',
            FEATURED_IMAGE_CAPTION: 'e.g., Fiddlers at a Cape Breton ceilidh',
            ARTICLE_CONTENT: 'Write your article content here...',
            ARTICLE_TAGS: 'e.g., Cape Breton, History, Traditional Music'
        },
        template: `<!-- Start of Article Page Content -->

<style>
    .article-page {
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
        background-color: var(--color-background);
        color: var(--color-text-primary);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }

    .article-page .article-header {
        text-align: center;
        margin-bottom: 30px;
        padding-bottom: 20px;
        border-bottom: 2px solid var(--color-primary);
    }

    .article-page .article-title {
        color: var(--color-primary);
        font-size: 2.5em;
        margin: 0 0 10px 0;
        line-height: 1.2;
        font-weight: 700;
    }

    .article-page .article-subtitle {
        font-size: 1.3em;
        color: var(--color-text-secondary);
        margin: 0 0 20px 0;
        font-style: italic;
    }

    .article-page .article-meta {
        display: flex;
        justify-content: center;
        gap: 20px;
        flex-wrap: wrap;
        font-size: 0.95em;
        color: var(--color-text-secondary);
    }

    .article-page .article-meta-item {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .article-page .featured-image-container {
        margin: 30px 0;
        text-align: center;
    }

    .article-page .featured-image {
        max-width: 100%;
        height: auto;
        border-radius: 12px;
        box-shadow: 0 4px 20px var(--color-shadow);
    }

    .article-page .image-caption {
        margin-top: 12px;
        font-size: 0.9em;
        color: var(--color-text-secondary);
        font-style: italic;
    }

    .article-page .article-content {
        line-height: 1.8;
        font-size: 1.1em;
    }

    .article-page .article-content p {
        margin-bottom: 1.5em;
    }

    .article-page .article-content h2 {
        color: var(--color-primary);
        margin-top: 2em;
        margin-bottom: 1em;
        font-size: 1.6em;
        border-bottom: 1px solid var(--color-border);
        padding-bottom: 0.5em;
    }

    .article-page .article-content h3 {
        color: var(--color-accent);
        margin-top: 1.5em;
        margin-bottom: 0.75em;
        font-size: 1.3em;
    }

    .article-page .article-tags {
        margin-top: 40px;
        padding-top: 20px;
        border-top: 1px solid var(--color-border);
    }

    .article-page .article-tags-label {
        font-weight: 600;
        color: var(--color-text-secondary);
        margin-right: 10px;
    }

    .article-page .tag {
        display: inline-block;
        background: var(--color-surface);
        color: var(--color-text-primary);
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 0.85em;
        margin: 4px;
        border: 1px solid var(--color-border);
    }

    .article-page .back-link {
        display: inline-block;
        margin-top: 30px;
        color: var(--color-accent);
        text-decoration: none;
        font-weight: 500;
    }

    .article-page .back-link:hover {
        opacity: 0.8;
    }

    @media (max-width: 768px) {
        .article-page .article-title {
            font-size: 1.8em;
        }
        .article-page .article-subtitle {
            font-size: 1.1em;
        }
        .article-page .article-meta {
            flex-direction: column;
            gap: 10px;
        }
    }
</style>

<div class="article-page">
    <div class="article-header">
        <h1 class="article-title">{{ARTICLE_TITLE}}</h1>
        <p class="article-subtitle">{{ARTICLE_SUBTITLE}}</p>
        <div class="article-meta">
            <span class="article-meta-item">
                ✍️ <strong>{{ARTICLE_AUTHOR}}</strong>
            </span>
            <span class="article-meta-item">
                📅 {{ARTICLE_DATE}}
            </span>
        </div>
    </div>

    <div class="featured-image-container">
        <img src="/api/assets/{{FEATURED_IMAGE_ASSET_ID}}/thumbnail" 
             alt="{{ARTICLE_TITLE}}" 
             class="featured-image"
             onerror="this.style.display='none'">
        <p class="image-caption">{{FEATURED_IMAGE_CAPTION}}</p>
    </div>

    <div class="article-content">
{{ARTICLE_CONTENT}}
    </div>

    <div class="article-tags">
        <span class="article-tags-label">🏷️ Tags:</span>
        {{ARTICLE_TAGS}}
    </div>

    <a href="/articles" class="back-link">← Back to Articles</a>
</div>

<!-- End of Article Page Content -->`
    },

    'book-index-page': {
        name: 'Book Index (tune list)',
        description:
            'Tune index for a book. Use page_type book-index. Each row: .tune-index-item with .tune-name containing the exact tune title (site links matches after load). Optional .stat-success / .stat-warning for counts.',
        placeholders: {
            BOOK_TITLE: '',
            BOOK_AUTHOR: '',
            PRODUCT_ID: '',
            BOOK_DESCRIPTION: '',
            TUNE_INDEX_ROWS: ''
        },
        hints: {
            BOOK_TITLE: 'e.g., Jerry Holland: The 2nd Collection',
            BOOK_AUTHOR: 'e.g., By Jerry Holland',
            PRODUCT_ID: 'Numeric product ID for “View book” / purchase links',
            BOOK_DESCRIPTION: 'Short intro paragraph (HTML allowed)',
            TUNE_INDEX_ROWS:
                'Repeat: <div class="tune-index-item tune-unavailable"><span class="tune-name">Tune Name</span><span class="tune-page">12</span></div> — hydration turns matches into links.'
        },
        template: `<!-- Start of Book Index Page Content -->

<style>
    .book-index-page { max-width: 1100px; margin: 0 auto; padding: 20px; color: var(--color-text-primary); }
    .book-index-header-compact { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 2px solid var(--color-border); }
    .book-index-header-compact h1 { margin: 0 0 6px 0; font-size: 1.65rem; color: var(--color-primary); }
    .book-author { margin: 0; color: var(--color-text-secondary); font-size: 0.95rem; }
    .btn-primary-compact { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 8px; background: var(--color-primary); color: #fff; text-decoration: none; font-weight: 600; }
    .btn-primary-compact:hover { opacity: 0.92; }
    .book-index-description { margin: 12px 0 16px; padding: 14px 16px; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; line-height: 1.55; }
    .book-index-stats-compact { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 20px; padding: 12px 14px; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; font-size: 0.9rem; color: var(--color-text-secondary); }
    .stat-success { color: var(--color-success, #2e7d32); }
    .stat-warning { color: var(--color-text-secondary); }
    .stat-divider { color: var(--color-border); }
    .book-index-content { background: var(--color-card-bg, #fff); border: 1px solid var(--color-border); border-radius: 10px; padding: 18px; }
    .tune-index-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 8px; }
    .tune-index-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 6px; text-decoration: none; color: inherit; }
    .tune-index-item.tune-unavailable { opacity: 0.75; }
    .tune-name { flex: 1; font-size: 0.92rem; font-weight: 500; }
    .tune-page { font-size: 0.85rem; color: var(--color-text-secondary); font-weight: 600; margin-left: 8px; }
</style>

<div class="book-index-page">
  <div class="book-index-header-compact">
    <div class="header-left">
      <h1>{{BOOK_TITLE}}</h1>
      <p class="book-author">{{BOOK_AUTHOR}}</p>
    </div>
    <div class="header-right">
      <a href="/product/{{PRODUCT_ID}}" class="btn-primary-compact">📚 View product</a>
    </div>
  </div>
  <div class="book-index-description">{{BOOK_DESCRIPTION}}</div>
  <div class="book-index-stats-compact">
    <span class="stat-compact stat-success"><strong>0</strong> available online</span>
    <span class="stat-divider">·</span>
    <span class="stat-compact stat-warning"><strong>0</strong> coming soon</span>
  </div>
  <div class="book-index-content">
    <div class="tune-index-grid">
{{TUNE_INDEX_ROWS}}
    </div>
  </div>
</div>

<!-- End of Book Index Page Content -->`
    },

    'cd-tracks-page': {
        name: 'CD / album track listing',
        description:
            'Track-by-track listing for a CD product. Use page_type general or article as needed. Paste track blocks into TRACK_LIST_HTML.',
        placeholders: {
            CD_TITLE: '',
            CD_ARTIST: '',
            PRODUCT_ID: '',
            CD_INTRO: '',
            TRACK_LIST_HTML: ''
        },
        hints: {
            CD_TITLE: 'e.g., Another Side of Cape Breton',
            CD_ARTIST: 'e.g., Johnny Wilmot',
            PRODUCT_ID: 'Product ID for purchase link',
            CD_INTRO: 'Blurb about the recording (HTML)',
            TRACK_LIST_HTML:
                'Use .track-item blocks: heading + tune lines with optional /page/ links. See default sample in template.'
        },
        template: `<!-- Start of CD Tracks Page Content -->

<style>
    .cd-tracks-page { max-width: 900px; margin: 0 auto; padding: 20px; color: var(--color-text-primary); }
    .cd-tracks-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 2px solid var(--color-border); }
    .cd-tracks-header h1 { margin: 0 0 6px 0; font-size: 1.65rem; color: var(--color-primary); }
    .cd-artist { margin: 0; color: var(--color-text-secondary); }
    .cd-btn { display: inline-flex; padding: 10px 16px; border-radius: 8px; background: var(--color-primary); color: #fff; text-decoration: none; font-weight: 600; }
    .cd-intro { margin: 0 0 20px; padding: 14px 16px; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; line-height: 1.55; }
    .tracks-container { display: flex; flex-direction: column; gap: 14px; }
    .track-item { padding: 14px 16px; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; border-left: 4px solid var(--color-accent); }
    .track-header { font-weight: 700; color: var(--color-primary); margin-bottom: 10px; }
    .track-tunes { margin-left: 8px; display: flex; flex-direction: column; gap: 6px; font-size: 0.95rem; }
    .cd-tracks-footer { margin-top: 24px; display: flex; flex-wrap: wrap; gap: 12px; }
</style>

<div class="cd-tracks-page">
  <div class="cd-tracks-header">
    <div>
      <h1>💿 {{CD_TITLE}}</h1>
      <p class="cd-artist">{{CD_ARTIST}}</p>
    </div>
    <a href="/product/{{PRODUCT_ID}}" class="cd-btn">🛒 View product</a>
  </div>
  <div class="cd-intro">{{CD_INTRO}}</div>
  <div class="tracks-container">
{{TRACK_LIST_HTML}}
  </div>
  <div class="cd-tracks-footer">
    <a href="/product/{{PRODUCT_ID}}" class="cd-btn">Purchase CD</a>
    <a href="/tunes" class="cd-btn" style="background: var(--color-text-secondary);">🎵 All tunes</a>
  </div>
</div>

<!-- End of CD Tracks Page Content -->`
    },

    'musician-biography': {
        name: 'Musician Biography Template',
        description: 'Profile page for fiddlers, composers, and musicians',
        placeholders: {
            MUSICIAN_NAME: '',
            MUSICIAN_SUBTITLE: '',
            MUSICIAN_YEARS: '',
            MUSICIAN_REGION: '',
            PORTRAIT_ASSET_ID: '',
            BIOGRAPHY_CONTENT: '',
            NOTABLE_TUNES: '',
            RECORDINGS: '',
            RELATED_PRODUCTS: ''
        },
        hints: {
            MUSICIAN_NAME: 'e.g., Winston "Scotty" Fitzgerald',
            MUSICIAN_SUBTITLE: 'e.g., Cape Breton Fiddle Legend',
            MUSICIAN_YEARS: 'e.g., 1914-1987',
            MUSICIAN_REGION: 'e.g., Cape Breton, Nova Scotia',
            PORTRAIT_ASSET_ID: 'e.g., 456',
            BIOGRAPHY_CONTENT: 'Write the biography here...',
            NOTABLE_TUNES: 'e.g., King George IV Strathspey, The Trip to Windsor',
            RECORDINGS: 'e.g., Celtic FS 17, Rounder Records',
            RELATED_PRODUCTS: 'Product IDs, comma-separated'
        },
        template: `<!-- Start of Musician Biography Content -->

<style>
    .musician-bio {
        max-width: 900px;
        margin: 0 auto;
        padding: 20px;
        background-color: var(--color-background);
        color: var(--color-text-primary);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }

    .musician-bio .bio-header {
        display: grid;
        grid-template-columns: 250px 1fr;
        gap: 30px;
        margin-bottom: 30px;
        padding-bottom: 30px;
        border-bottom: 2px solid var(--color-primary);
    }

    .musician-bio .portrait-container {
        text-align: center;
    }

    .musician-bio .portrait {
        width: 100%;
        max-width: 250px;
        height: auto;
        border-radius: 12px;
        box-shadow: 0 4px 20px var(--color-shadow);
        border: 3px solid var(--color-primary);
    }

    .musician-bio .bio-intro {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .musician-bio .musician-name {
        color: var(--color-primary);
        font-size: 2.2em;
        margin: 0 0 8px 0;
        line-height: 1.2;
        font-weight: 700;
    }

    .musician-bio .musician-subtitle {
        font-size: 1.2em;
        color: var(--color-accent);
        margin: 0 0 15px 0;
        font-style: italic;
    }

    .musician-bio .musician-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        color: var(--color-text-secondary);
    }

    .musician-bio .meta-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.95em;
    }

    .musician-bio .bio-section {
        margin: 30px 0;
    }

    .musician-bio .section-title {
        color: var(--color-primary);
        font-size: 1.5em;
        margin: 0 0 15px 0;
        padding-bottom: 8px;
        border-bottom: 2px solid var(--color-primary);
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .musician-bio .bio-content {
        line-height: 1.8;
        font-size: 1.05em;
    }

    .musician-bio .bio-content p {
        margin-bottom: 1.2em;
    }

    .musician-bio .tune-list, .musician-bio .recording-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 10px;
    }

    .musician-bio .tune-list li, .musician-bio .recording-list li {
        background: var(--color-surface);
        padding: 10px 15px;
        border-radius: 8px;
        border: 1px solid var(--color-border);
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .musician-bio .back-link {
        display: inline-block;
        margin-top: 30px;
        color: var(--color-accent);
        text-decoration: none;
        font-weight: 500;
    }

    .musician-bio .back-link:hover {
        opacity: 0.8;
    }

    @media (max-width: 768px) {
        .musician-bio .bio-header {
            grid-template-columns: 1fr;
            text-align: center;
        }
        .musician-bio .portrait-container {
            order: -1;
        }
        .musician-bio .portrait {
            max-width: 200px;
        }
        .musician-bio .musician-meta {
            justify-content: center;
        }
        .musician-bio .tune-list, .musician-bio .recording-list {
            grid-template-columns: 1fr;
        }
    }
</style>

<div class="musician-bio">
    <div class="bio-header">
        <div class="portrait-container">
            <img src="/api/assets/{{PORTRAIT_ASSET_ID}}/thumbnail" 
                 alt="{{MUSICIAN_NAME}}" 
                 class="portrait"
                 onerror="this.src='https://placehold.co/250x300/21262d/8b949e?text=Photo'; this.style.border='none';">
        </div>
        <div class="bio-intro">
            <h1 class="musician-name">{{MUSICIAN_NAME}}</h1>
            <p class="musician-subtitle">{{MUSICIAN_SUBTITLE}}</p>
            <div class="musician-meta">
                <span class="meta-item">📅 {{MUSICIAN_YEARS}}</span>
                <span class="meta-item">📍 {{MUSICIAN_REGION}}</span>
            </div>
        </div>
    </div>

    <div class="bio-section">
        <h2 class="section-title">🎻 Biography</h2>
        <div class="bio-content">
{{BIOGRAPHY_CONTENT}}
        </div>
    </div>

    <div class="bio-section">
        <h2 class="section-title">🎵 Notable Tunes</h2>
        <ul class="tune-list">
            <!-- Add tunes here -->
            <li>🎼 {{NOTABLE_TUNES}}</li>
        </ul>
    </div>

    <div class="bio-section">
        <h2 class="section-title">💿 Recordings</h2>
        <ul class="recording-list">
            <!-- Add recordings here -->
            <li>📀 {{RECORDINGS}}</li>
        </ul>
    </div>

    <a href="/articles" class="back-link">← Back to Articles</a>
</div>

<!-- End of Musician Biography Content -->`
    },

    'album-review': {
        name: 'Album Review Template',
        description: 'Review page for CDs, recordings, and music releases',
        placeholders: {
            ALBUM_TITLE: '',
            ALBUM_ARTIST: '',
            RELEASE_YEAR: '',
            RECORD_LABEL: '',
            ALBUM_COVER_ASSET_ID: '',
            REVIEW_AUTHOR: '',
            REVIEW_DATE: '',
            RATING: '',
            REVIEW_CONTENT: '',
            TRACK_LIST: '',
            PRODUCT_ID: ''
        },
        hints: {
            ALBUM_TITLE: 'e.g., Cape Breton Fiddle Music',
            ALBUM_ARTIST: 'e.g., Buddy MacMaster',
            RELEASE_YEAR: 'e.g., 1989',
            RECORD_LABEL: 'e.g., Rounder Records',
            ALBUM_COVER_ASSET_ID: 'e.g., 789',
            REVIEW_AUTHOR: 'e.g., Paul Cranford',
            REVIEW_DATE: 'e.g., January 20, 2026',
            RATING: 'e.g., 5/5 or ⭐⭐⭐⭐⭐',
            REVIEW_CONTENT: 'Write the review here...',
            TRACK_LIST: 'e.g., 1. King George IV, 2. Reel of Tulloch...',
            PRODUCT_ID: 'e.g., 42 (if available in shop)'
        },
        template: `<!-- Start of Album Review Content -->

<style>
    .album-review {
        max-width: 900px;
        margin: 0 auto;
        padding: 20px;
        background-color: var(--color-background);
        color: var(--color-text-primary);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }

    .album-review .review-header {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 30px;
        margin-bottom: 30px;
        padding-bottom: 30px;
        border-bottom: 2px solid var(--color-primary);
    }

    .album-review .album-cover-container {
        text-align: center;
    }

    .album-review .album-cover {
        width: 100%;
        max-width: 280px;
        height: auto;
        border-radius: 8px;
        box-shadow: 0 8px 30px var(--color-shadow);
    }

    .album-review .album-info {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .album-review .album-title {
        color: var(--color-primary);
        font-size: 2em;
        margin: 0 0 8px 0;
        line-height: 1.2;
        font-weight: 700;
    }

    .album-review .album-artist {
        font-size: 1.4em;
        color: var(--color-accent);
        margin: 0 0 15px 0;
    }

    .album-review .album-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        color: var(--color-text-secondary);
        margin-bottom: 20px;
    }

    .album-review .meta-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.95em;
    }

    .album-review .rating {
        font-size: 1.8em;
        color: var(--color-warning, #f59e0b);
        margin: 10px 0;
    }

    .album-review .review-meta {
        font-size: 0.9em;
        color: var(--color-text-secondary);
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid var(--color-border);
    }

    .album-review .review-section {
        margin: 30px 0;
    }

    .album-review .section-title {
        color: var(--color-primary);
        font-size: 1.4em;
        margin: 0 0 15px 0;
        padding-bottom: 8px;
        border-bottom: 2px solid var(--color-primary);
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .album-review .review-content {
        line-height: 1.8;
        font-size: 1.05em;
    }

    .album-review .review-content p {
        margin-bottom: 1.2em;
    }

    .album-review .track-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 8px;
    }

    .album-review .track-list li {
        background: var(--color-surface);
        padding: 10px 15px;
        border-radius: 6px;
        border: 1px solid var(--color-border);
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .album-review .track-number {
        background: var(--color-primary);
        color: var(--color-background);
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.85em;
        font-weight: 600;
        flex-shrink: 0;
    }

    .album-review .buy-section {
        margin-top: 30px;
        padding: 20px;
        background: linear-gradient(135deg, var(--color-surface), var(--color-background));
        border-radius: 12px;
        border: 1px solid var(--color-border);
        text-align: center;
    }

    .album-review .buy-btn {
        display: inline-block;
        background: var(--color-primary);
        color: white;
        padding: 12px 30px;
        border-radius: 8px;
        text-decoration: none;
        font-weight: 600;
        font-size: 1.1em;
        transition: all 0.3s ease;
    }

    .album-review .buy-btn:hover {
        opacity: 0.9;
        transform: translateY(-2px);
    }

    .album-review .back-link {
        display: inline-block;
        margin-top: 30px;
        color: var(--color-accent);
        text-decoration: none;
        font-weight: 500;
    }

    .album-review .back-link:hover {
        opacity: 0.8;
    }

    @media (max-width: 768px) {
        .album-review .review-header {
            grid-template-columns: 1fr;
            text-align: center;
        }
        .album-review .album-cover-container {
            order: -1;
        }
        .album-review .album-cover {
            max-width: 200px;
        }
        .album-review .album-meta {
            justify-content: center;
        }
        .album-review .track-list {
            grid-template-columns: 1fr;
        }
    }
</style>

<div class="album-review">
    <div class="review-header">
        <div class="album-cover-container">
            <img src="/api/assets/{{ALBUM_COVER_ASSET_ID}}/thumbnail" 
                 alt="{{ALBUM_TITLE}} album cover" 
                 class="album-cover"
                 onerror="this.src='https://placehold.co/280x280/21262d/8b949e?text=Album+Cover';">
        </div>
        <div class="album-info">
            <h1 class="album-title">{{ALBUM_TITLE}}</h1>
            <p class="album-artist">{{ALBUM_ARTIST}}</p>
            <div class="album-meta">
                <span class="meta-item">📅 {{RELEASE_YEAR}}</span>
                <span class="meta-item">🏷️ {{RECORD_LABEL}}</span>
            </div>
            <div class="rating">{{RATING}}</div>
            <div class="review-meta">
                ✍️ Reviewed by <strong>{{REVIEW_AUTHOR}}</strong> on {{REVIEW_DATE}}
            </div>
        </div>
    </div>

    <div class="review-section">
        <h2 class="section-title">📝 Review</h2>
        <div class="review-content">
{{REVIEW_CONTENT}}
        </div>
    </div>

    <div class="review-section">
        <h2 class="section-title">🎵 Track List</h2>
        <ul class="track-list">
            <!-- Add tracks here -->
            <li><span class="track-number">1</span> {{TRACK_LIST}}</li>
        </ul>
    </div>

    <div class="buy-section">
        <p style="margin: 0 0 15px 0; color: var(--color-text-secondary);">Available in our shop</p>
        <a href="/product/{{PRODUCT_ID}}" class="buy-btn">🛒 Buy This Album</a>
    </div>

    <a href="/articles" class="back-link">← Back to Articles</a>
</div>

<!-- End of Album Review Content -->`
    }
};

